import { useSelector } from 'react-redux'
import { hasPermission, hasAnyPermission, hasAllPermissions } from 'utils/permissionConfig'

/**
 * Custom hook to check user permissions
 * @returns {Object} Permission checking functions
 */
const usePermission = () => {
  const userPermissions = useSelector(state => state.auth?.permissions || [])
  const userRole = useSelector(state => state.auth?.role || null)

  /**
   * Check if user has specific permission
   * @param {String} permission - Permission to check
   * @returns {Boolean}
   */
  const checkPermission = (permission) => {
    return hasPermission(userPermissions, permission)
  }

  /**
   * Check if user has any of the given permissions
   * @param {Array} permissions - Array of permissions (OR logic)
   * @returns {Boolean}
   */
  const checkAnyPermission = (permissions) => {
    return hasAnyPermission(userPermissions, permissions)
  }

  /**
   * Check if user has all of the given permissions
   * @param {Array} permissions - Array of permissions (AND logic)
   * @returns {Boolean}
   */
  const checkAllPermissions = (permissions) => {
    return hasAllPermissions(userPermissions, permissions)
  }

  /**
   * Check if user has specific role
   * @param {String|Array} roles - Role(s) to check
   * @returns {Boolean}
   */
  const checkRole = (roles) => {
    if (!roles) return true
    if (Array.isArray(roles)) {
      return roles.includes(userRole)
    }
    return userRole === roles
  }

  return {
    permissions: userPermissions,
    role: userRole,
    hasPermission: checkPermission,
    hasAnyPermission: checkAnyPermission,
    hasAllPermissions: checkAllPermissions,
    hasRole: checkRole,
    isAdmin: userRole === 'Admin',
    isProvider: userRole === 'Provider',
    isProvince: userRole === 'Province',
    isMonitor: userRole === 'Monitor',
  }
}

export default usePermission
